// frontend/src/pages/PlantillasPPTPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import contentService from '../services/contentService';

import Loading from '../components/Loading';
const GRUPO_COLORS = {
  General:   { bg: '#f0fdf4', border: '#86efac', badge: '#16a34a' },
  Totales:   { bg: '#eff6ff', border: '#93c5fd', badge: '#2563eb' },
  Instagram: { bg: '#fdf2f8', border: '#f0abfc', badge: '#a21caf' },
  TikTok:    { bg: '#fff7ed', border: '#fdba74', badge: '#ea580c' },
};

function TokenChip({ token, desc, grupo }) {
  const [copied, setCopied] = useState(false);
  const c = GRUPO_COLORS[grupo] || GRUPO_COLORS.General;
  const copy = () => {
    navigator.clipboard.writeText(`{{${token}}}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div onClick={copy} title="Clic para copiar" style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px',
      border: `1px solid ${c.border}`, borderRadius: 8, background: c.bg,
      cursor: 'pointer', transition: 'opacity .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      <code style={{
        fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
        color: c.badge, whiteSpace: 'nowrap', flexShrink: 0,
        background: 'white', padding: '1px 6px', borderRadius: 4,
        border: `1px solid ${c.border}`,
      }}>
        {`{{${token}}}`}
      </code>
      <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>{desc}</span>
      {copied && <span style={{ fontSize: 11, color: c.badge, fontWeight: 700, marginLeft: 'auto', flexShrink: 0 }}>✓ copiado</span>}
    </div>
  );
}

export default function PlantillasPPTPage() {
  const [templates, setTemplates]   = useState([]);
  const [catalog, setCatalog]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [nombre, setNombre]         = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [clienteSlug, setClienteSlug] = useState('');
  const [file, setFile]             = useState(null);
  const [catOpen, setCatOpen]       = useState(true);
  const fileRef = useRef();
  const [dragOver, setDragOver]     = useState(false);

  useEffect(() => {
    Promise.all([
      contentService.getPPTTemplates(),
      contentService.getPPTTokensCatalog(),
    ]).then(([tRes, cRes]) => {
      setTemplates(tRes.data.templates || []);
      setCatalog(cRes.data.tokens || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !nombre.trim()) {
      setUploadError('Nombre y archivo son obligatorios.');
      return;
    }
    setUploading(true); setUploadError('');
    try {
      // Paso 1: pedir presigned URL al backend
      const { data } = await contentService.getPPTPresignedUrl(file.name, clienteSlug.trim() || 'general');
      const { presigned_url, public_url } = data;

      // Paso 2: subir archivo DIRECTAMENTE a DigitalOcean Spaces
      const putResp = await contentService.uploadFileToSpaces(presigned_url, file);
      if (!putResp.ok) throw new Error(`Error al subir a Spaces: ${putResp.status}`);

      // Paso 3: guardar registro en la BD con la URL pública
      await contentService.savePPTTemplate(nombre.trim(), descripcion, clienteSlug.trim(), public_url);

      const updated = await contentService.getPPTTemplates();
      setTemplates(updated.data.templates || []);
      setNombre(''); setDescripcion(''); setClienteSlug(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setUploadError(err?.response?.data?.error || err?.message || 'Error al subir la plantilla.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      await contentService.deletePPTTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {
      alert('Error al eliminar.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.pptx')) setFile(f);
  };

  // Agrupar catálogo
  const grupos = [...new Set(catalog.map(t => t.grupo))];

  if (loading) return <Loading fullScreen={false} mensaje="Cargando" />;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px', fontFamily: 'inherit' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#002421', marginBottom: 4 }}>Plantillas PPT</h1>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 32 }}>
        Sube tu plantilla <code>.pptx</code> con tokens <code>{'{{token}}'}</code> y genera reportes rellenos con data real desde Reporte Geves.
      </p>

      {/* ── Catálogo de tokens ── */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 32, overflow: 'hidden' }}>
        <button onClick={() => setCatOpen(v => !v)} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit',
        }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#002421' }}>📋 Catálogo de tokens disponibles</span>
          <span style={{ fontSize: 14, color: '#94a3b8' }}>{catOpen ? '▲' : '▼'}</span>
        </button>
        {catOpen && (
          <div style={{ padding: '0 20px 20px' }}>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              Copia el token y pégalo en tu PowerPoint donde quieras que aparezca el dato. Haz clic en un token para copiarlo.
            </p>
            {grupos.map(grupo => (
              <div key={grupo} style={{ marginBottom: 20 }}>
                <div style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: GRUPO_COLORS[grupo]?.badge || '#475569', marginBottom: 8,
                }}>{grupo}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 6 }}>
                  {catalog.filter(t => t.grupo === grupo).map(t => (
                    <TokenChip key={t.token} {...t} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Subir plantilla ── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#002421', marginBottom: 20 }}>Subir nueva plantilla</h2>
        <form onSubmit={handleUpload}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Nombre *</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)}
                placeholder="Ej: Reporte Mensual Maicao" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Cliente / slug (opcional)</label>
              <input value={clienteSlug} onChange={e => setClienteSlug(e.target.value)}
                placeholder="Ej: maicao" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Descripción (opcional)</label>
            <input value={descripcion} onChange={e => setDescripcion(e.target.value)}
              placeholder="Ej: Template ejecutivo con slides de IG y TikTok" style={inputStyle} />
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? '#002421' : '#cbd5e1'}`,
              borderRadius: 10, padding: '28px 20px', textAlign: 'center',
              background: dragOver ? '#f0fdf4' : '#f8fafc',
              cursor: 'pointer', marginBottom: 16, transition: 'all .2s',
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
            {file ? (
              <div>
                <span style={{ fontWeight: 700, color: '#002421' }}>{file.name}</span>
                <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>
                  ({(file.size / 1024).toFixed(0)} KB)
                </span>
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 600, color: '#002421', marginBottom: 4 }}>
                  Arrastra tu .pptx aquí o haz clic para seleccionar
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Solo archivos .pptx</div>
              </>
            )}
            <input ref={fileRef} type="file" accept=".pptx" style={{ display: 'none' }}
              onChange={e => setFile(e.target.files[0] || null)} />
          </div>

          {uploadError && (
            <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{uploadError}</div>
          )}

          <button type="submit" disabled={uploading} style={{
            background: '#002421', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1, fontFamily: 'inherit',
          }}>
            {uploading ? 'Subiendo a Spaces…' : 'Guardar plantilla'}
          </button>
        </form>
      </div>

      {/* ── Plantillas guardadas ── */}
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#002421', marginBottom: 16 }}>
          Mis plantillas ({templates.length})
        </h2>
        {!templates.length ? (
          <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>
            Aún no tienes plantillas guardadas.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {templates.map(t => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 10, padding: '14px 18px',
              }}>
                <span style={{ fontSize: 22 }}>📊</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#002421' }}>{t.nombre}</div>
                  {t.descripcion && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t.descripcion}</div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {t.cliente_slug && <span style={{ marginRight: 8 }}>🏷 {t.cliente_slug}</span>}
                    {new Date(t.creado_en).toLocaleDateString('es-CL')}
                  </div>
                </div>
                {t.archivo_url && (
                  <a href={t.archivo_url} target="_blank" rel="noreferrer" style={{
                    fontSize: 12, color: '#6366f1', textDecoration: 'none', fontWeight: 600,
                    padding: '5px 12px', border: '1px solid #e0e7ff', borderRadius: 6,
                    background: '#f5f3ff', whiteSpace: 'nowrap',
                  }}>
                    ↓ Descargar
                  </a>
                )}
                <button onClick={() => handleDelete(t.id, t.nombre)} style={{
                  background: 'none', border: '1px solid #fecaca', borderRadius: 6,
                  color: '#ef4444', fontSize: 12, fontWeight: 600, padding: '5px 12px',
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#475569', marginBottom: 6,
};
const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
  outline: 'none', color: '#1e293b',
};
